/**
 * Per-mob path following on top of PathFinder (vanilla PathNavigation):
 * straight-line shortcut when the way is clear, otherwise a cached A* path that
 * is recomputed on a timer, when the target moves more than a block, or when
 * the mob is stuck. A global per-tick node budget keeps crowds cheap. Also
 * door handling (open / close, or break on hard) and a flyer steering helper.
 */

import { AIR, DOOR_STATE, doorId } from '../../blocks';
import type { World } from '../../world/chunk';
import {
  DOORS_BREAK, DOORS_NONE, DOORS_OPEN, NO_NODE, PATH_FAIL, PathFinder, PathResult, type NavSpec,
} from './pathfinder';
import { K_DOOR_CLOSED, K_LAVA, KIND, TOP } from './nodes';

export { DOORS_BREAK, DOORS_NONE, DOORS_OPEN } from './pathfinder';
export type { NavSpec } from './pathfinder';

/** World edits the navigator may ask for (doors); absent = doors stay shut. */
export interface NavEnv {
  setBlock?(x: number, y: number, z: number, id: number): void;
  /** Door being broken: stage 0..9 (-1 = progress dropped). Hook for a crack animation. */
  crack?(x: number, y: number, z: number, stage: number): void;
}

/** Where to steer this tick. */
export interface Steer { dx: number; dy: number; dz: number; jump: boolean }
export const newSteer = (): Steer => ({ dx: 0, dy: 0, dz: 0, jump: false });

/** Nodes all navigators together may expand per tick. */
export const NAV_BUDGET_PER_TICK = 1800;
/** Ticks a door takes to break (vanilla BreakDoorGoal on hard). */
export const DOOR_BREAK_TICKS = 240;
/** Searches smaller than this are not worth starting from what the budget has left. */
const MIN_SEARCH = 40;
const MAX_STRAIGHT = 24;

const finder = new PathFinder();
let budgetTick = -1;
let budgetLeft = 0;
/** Nodes the budget has left this tick. */
export function navBudgetLeft(tick: number): number {
  if (tick !== budgetTick) { budgetTick = tick; budgetLeft = NAV_BUDGET_PER_TICK; }
  return budgetLeft;
}
/** Total searches run (smoke / profiling). */
export const navStats = { searches: 0, nodes: 0 };

export function makeSpec(width: number, height: number): NavSpec {
  return {
    fw: Math.max(1, Math.ceil(width - 1e-4)), fh: Math.max(1, Math.ceil(height - 1e-4)),
    maxDrop: 3, waterPenalty: 8, swim: false, lava: false, climb: false, doors: DOORS_NONE,
  };
}

export class Navigator {
  readonly spec: NavSpec;
  private res: PathResult | null = null;
  private len = 0;
  private idx = 0;
  private complete = false;
  private gx = 1e9; private gy = 0; private gz = 0;
  private searchedAt = -100;
  private repathAt = 0;
  private noPathUntil = 0;
  private lastCall = -100;
  // stuck detection
  private markTick = 0; private markX = 0; private markZ = 0;
  private noStraightUntil = 0;
  private straightAt = 0;
  private straightOk = false;
  // doors
  private doorX = 0; private doorY = 0; private doorZ = 0; private doorOpened = false;
  private breakX = 0; private breakY = -1; private breakZ = 0; private breakTicks = 0;

  constructor(width: number, height: number) { this.spec = makeSpec(width, height); }

  /** Drop the cached path (target changed for good). */
  reset(): void { this.len = 0; this.idx = 0; this.gx = 1e9; this.straightAt = 0; }

  get hasPath(): boolean { return this.len > 0; }
  get pathLength(): number { return this.len; }

  /**
   * Steer from (ax, ay, az) toward (tx, ty, tz). Returns false when no route
   * exists (callers fall back to plain steering); otherwise fills `out`.
   * `range` bounds the search radius, `maxNodes` its size.
   */
  update(
    w: World, env: NavEnv | null, tick: number, id: number,
    ax: number, ay: number, az: number, tx: number, ty: number, tz: number,
    range: number, maxNodes: number, out: Steer,
  ): boolean {
    const s = this.spec;
    if (tick - this.lastCall > 3) { this.len = 0; this.idx = 0; this.markTick = tick; this.markX = ax; this.markZ = az; }
    this.lastCall = tick;
    const dxT = tx - ax, dzT = tz - az;
    out.dx = dxT; out.dz = dzT; out.dy = ty - ay; out.jump = false;
    if (s.doors !== DOORS_NONE) this.closeBehind(w, env, ax, az);
    if (dxT * dxT + dzT * dzT < 0.04) return true;

    // stuck: no progress for 20 ticks while asked to move
    if (tick - this.markTick >= 20) {
      const mx = ax - this.markX, mz = az - this.markZ;
      if (mx * mx + mz * mz < 0.04 && this.breakY < 0 && dxT * dxT + dzT * dzT > 4) {
        this.noStraightUntil = tick + 40;
        this.repathAt = 0; this.searchedAt = -100;
      }
      this.markTick = tick; this.markX = ax; this.markZ = az;
    }

    const sy = Math.floor(ay + 0.01);
    // straight line shortcut
    if (tick >= this.noStraightUntil) {
      if (tick >= this.straightAt) {
        this.straightOk = this.straight(w, ax, sy, az, tx, tz);
        this.straightAt = tick + 4 + (id & 3);
      }
      if (this.straightOk) { this.len = 0; return true; }
    }

    // (re)compute
    const moved = Math.abs(tx - this.gx) + Math.abs(tz - this.gz) > 1 || Math.abs(ty - this.gy) > 2;
    const need = this.len === 0 || (this.idx >= this.len && !this.complete)
      || (tick >= this.repathAt && tick - this.searchedAt >= 8)
      || (moved && tick - this.searchedAt >= 8);
    if (need && !(this.len === 0 && tick < this.noPathUntil)) {
      const left = navBudgetLeft(tick);
      if (left >= MIN_SEARCH) {
        if (!this.res) this.res = new PathResult();
        finder.bind(w, s);
        const st = finder.search(w, s, finder.cellOf(ax, s), sy, finder.cellOf(az, s),
          finder.cellOf(tx, s), Math.floor(ty + 0.01), finder.cellOf(tz, s), Math.min(left, maxNodes), range, this.res);
        budgetLeft -= this.res.expanded;
        navStats.searches++; navStats.nodes += this.res.expanded;
        this.searchedAt = tick;
        this.repathAt = tick + 20 + (id & 7);
        this.gx = tx; this.gy = ty; this.gz = tz;
        if (st === PATH_FAIL) {
          this.len = 0; this.noPathUntil = tick + 20 + (id & 7);
          return false;
        }
        this.len = this.res.len; this.idx = 0; this.complete = this.res.complete;
      } else if (this.len === 0) return false;
    }
    if (this.len === 0 || !this.res) return false;

    // advance past reached nodes
    const r = this.res;
    const reach = Math.max(0.45, s.fw * 0.5);
    let i = this.idx;
    while (i < this.len) {
      const wx = finder.centreOf(r.x[i], s), wz = finder.centreOf(r.z[i], s);
      const ex = wx - ax, ez = wz - az;
      if (ex * ex + ez * ez < reach * reach && Math.abs(r.y[i] - ay) < 1.1) i++;
      else break;
    }
    this.idx = i;
    if (i >= this.len) return true; // path spent: final approach straight at the target
    // far above / below the route (knocked off a ledge): replan next chance
    if (Math.abs(r.y[i] - ay) > 2.6) { this.repathAt = 0; }
    const wx = finder.centreOf(r.x[i], s), wz = finder.centreOf(r.z[i], s), wy = r.y[i];
    out.dx = wx - ax; out.dz = wz - az; out.dy = wy - ay;
    out.jump = wy > ay + 0.3 && out.dx * out.dx + out.dz * out.dz < 2.6;
    if (s.doors !== DOORS_NONE && env) this.handleDoors(w, env, i, ax, az, out);
    return true;
  }

  // ------------------------------------------------------------ straight

  /** Is the straight run toward (tx, tz) made only of plain, free steps? */
  private straight(w: World, ax: number, ay: number, az: number, tx: number, tz: number): boolean {
    const s = this.spec;
    finder.bind(w, s);
    let x = finder.cellOf(ax, s), z = finder.cellOf(az, s), y = ay;
    const gx = finder.cellOf(tx, s), gz = finder.cellOf(tz, s);
    const ddx = gx - x, ddz = gz - z;
    const nx = Math.abs(ddx), nz = Math.abs(ddz);
    if (nx + nz > 60) return false;
    const sgx = ddx < 0 ? -1 : 1, sgz = ddz < 0 ? -1 : 1;
    let ix = 0, iz = 0, steps = 0;
    while ((ix < nx || iz < nz) && steps++ < MAX_STRAIGHT) {
      let ny: number;
      if ((0.5 + ix) * nz < (0.5 + iz) * nx) { ny = finder.step(x, y, z, sgx, 0); x += sgx; ix++; }
      else { ny = finder.step(x, y, z, 0, sgz); z += sgz; iz++; }
      if (ny === NO_NODE || finder.pen > 0 || finder.sawDoor) return false;
      y = ny;
    }
    return true;
  }

  // --------------------------------------------------------------- doors

  /** Closed door in the column at a cell: its bottom y, or NO_NODE. */
  private doorBottom(w: World, x: number, y: number, z: number): number {
    for (let dy = 0; dy < 2; dy++) {
      const id = w.getBlock(x, y + dy, z);
      if (KIND[id] === K_DOOR_CLOSED) return DOOR_STATE[id]!.top ? y + dy - 1 : y + dy;
    }
    return NO_NODE;
  }

  private handleDoors(w: World, env: NavEnv, i: number, ax: number, az: number, out: Steer): void {
    const s = this.spec, r = this.res!;
    for (let k = i; k < Math.min(this.len, i + 2); k++) {
      const wx = finder.centreOf(r.x[k], s), wz = finder.centreOf(r.z[k], s);
      if ((wx - ax) * (wx - ax) + (wz - az) * (wz - az) > 6.25) break;
      const cx = Math.floor(wx), cz = Math.floor(wz);
      const by = this.doorBottom(w, cx, r.y[k], cz);
      if (by === NO_NODE) continue;
      if (s.doors === DOORS_OPEN) {
        if (!env.setBlock) return;
        this.toggleDoor(w, env, cx, by, cz, true);
        this.doorX = cx; this.doorY = by; this.doorZ = cz; this.doorOpened = true;
      } else if (s.doors === DOORS_BREAK) {
        if (this.breakX !== cx || this.breakY !== by || this.breakZ !== cz) {
          this.breakX = cx; this.breakY = by; this.breakZ = cz; this.breakTicks = 0;
        }
        out.dx = cx + 0.5 - ax; out.dz = cz + 0.5 - az;
        if (++this.breakTicks % 24 === 0) env.crack?.(cx, by, cz, Math.min(9, Math.floor(this.breakTicks * 10 / DOOR_BREAK_TICKS)));
        if (this.breakTicks >= DOOR_BREAK_TICKS && env.setBlock) {
          env.setBlock(cx, by, cz, AIR); env.setBlock(cx, by + 1, cz, AIR);
          env.crack?.(cx, by, cz, -1);
          this.breakY = -1; this.breakTicks = 0;
        }
      }
      return;
    }
    if (this.breakY >= 0) { env.crack?.(this.breakX, this.breakY, this.breakZ, -1); this.breakY = -1; this.breakTicks = 0; }
  }

  private toggleDoor(w: World, env: NavEnv, x: number, by: number, z: number, open: boolean): void {
    const b = DOOR_STATE[w.getBlock(x, by, z)];
    if (!b || b.open === open) return;
    env.setBlock!(x, by, z, doorId(b.kind, b.facing, b.hinge, open, false));
    const t = DOOR_STATE[w.getBlock(x, by + 1, z)];
    if (t) env.setBlock!(x, by + 1, z, doorId(t.kind, t.facing, t.hinge, open, true));
  }

  /** Shut a door this mob opened once it has walked two blocks past it. */
  private closeBehind(w: World, env: NavEnv | null, ax: number, az: number): void {
    if (!this.doorOpened || !env?.setBlock) return;
    const dx = this.doorX + 0.5 - ax, dz = this.doorZ + 0.5 - az;
    if (dx * dx + dz * dz < 6.25) return;
    this.toggleDoor(w, env, this.doorX, this.doorY, this.doorZ, false);
    this.doorOpened = false;
  }
}

/**
 * Flyers (ghast, blaze, bat, phantom): straight toward the target, bending up
 * and sideways around blocks within `look` blocks ahead. Writes a unit vector.
 */
export function steerFlying(
  w: World, ax: number, ay: number, az: number, tx: number, ty: number, tz: number, look: number, out: Steer,
): void {
  let dx = tx - ax, dy = ty - ay, dz = tz - az;
  const l = Math.sqrt(dx * dx + dy * dy + dz * dz);
  out.jump = false;
  if (l < 1e-4) { out.dx = out.dy = out.dz = 0; return; }
  dx /= l; dy /= l; dz /= l;
  const blocked = (vx: number, vy: number, vz: number): boolean => {
    for (let d = 1; d <= look; d++) {
      const id = w.getBlock(Math.floor(ax + vx * d), Math.floor(ay + vy * d), Math.floor(az + vz * d));
      if (TOP[id] > 0.5 || KIND[id] === K_LAVA) return true;
    }
    return false;
  };
  if (blocked(dx, dy, dz)) {
    // try climbing, then yaw offsets of 45 / 90 degrees either side
    const c = 0.7071;
    const tries = [
      [dx, 1, dz], [dx * c - dz * c, dy, dx * c + dz * c], [dx * c + dz * c, dy, -dx * c + dz * c],
      [-dz, dy, dx], [dz, dy, -dx],
    ];
    for (const [vx, vy, vz] of tries) {
      if (!blocked(vx, vy, vz)) { dx = vx; dy = vy; dz = vz; break; }
    }
  }
  const n = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  out.dx = dx / n; out.dy = dy / n; out.dz = dz / n;
}
